import React, { useState } from "react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { onLoginRequested } from "../../actions/authAction";
import { EyeIcon, EyeOffIcon, HeartIcon, UserGroupIcon, ClipboardListIcon } from "@heroicons/react/outline";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import Logo from "../../assets/cellies.png";
import "./Login.css";
import SsoButtons from "../../components/SsoButtons";
import { ssoProviders } from "../../utils/sso";

const Login = ({ error, loginRequested, onLoginRequested: login }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [ssoError, setSsoError] = useState("");
    const hasSso = ssoProviders().length > 0;
    // Hide the password form once password sign-in is retired.
    const passwordEnabled = process.env.REACT_APP_PASSWORD_LOGIN !== 'disabled';

    const handleSsoToken = (provider, idToken) => {
        setSsoError("");
        login({ provider, idToken });
    };

    const handleLogin = (e) => {
        e.preventDefault();
        login({ email, password });
    };

    return (
        <div className="login-page">
            <div className="login-orb login-orb-one" />
            <div className="login-orb login-orb-two" />
            <div className="login-orb login-orb-three" />

            <main className="login-layout">
                <section className="login-story">
                    <div className="login-brand">
                        <img className="login-brand-logo" src={Logo} alt="Cell Report logo" />
                        <div>
                            <p className="login-brand-caption">ANFGC Halifax</p>
                            <h1 className="login-brand-title">Cell Report</h1>
                        </div>
                    </div>

                    <p className="login-kicker">
                        <HeartIcon className="login-kicker-icon" />
                        Ministry centered
                    </p>
                    <h2 className="login-heading">Welcome back to your cell family dashboard</h2>
                    <p className="login-subheading">
                        Sign in to update attendance, capture testimonies, and keep your weekly fellowship reports organized.
                    </p>

                    <div className="login-benefits">
                        <article className="login-benefit">
                            <UserGroupIcon className="login-benefit-icon" />
                            <div>
                                <h3>People First</h3>
                                <p>Focus on active members, visitors, and follow-up care each week.</p>
                            </div>
                        </article>
                        <article className="login-benefit">
                            <ClipboardListIcon className="login-benefit-icon" />
                            <div>
                                <h3>Simple Reporting</h3>
                                <p>Submit meeting reports in minutes with clean, guided sections.</p>
                            </div>
                        </article>
                    </div>
                </section>

                <section className="login-form-wrap">
                    <div className="login-card">
                        <h3 className="login-card-title">Sign In</h3>
                        <p className="login-card-subtitle">
                            {hasSso ? "Sign in with Google or Apple." : "Use your email or username to continue."}
                        </p>

                        {(error || ssoError) && (
                            <div className="login-error" role="alert">
                                <strong>Error:</strong> <span>{ssoError || error}</span>
                            </div>
                        )}

                        {hasSso && (
                            <div className="mb-2">
                                <SsoButtons onToken={handleSsoToken} onError={setSsoError} disabled={loginRequested} />
                            </div>
                        )}

                        {hasSso && passwordEnabled && (
                            <div className="my-5 flex items-center gap-3 text-xs font-medium uppercase tracking-wide text-gray-400">
                                <span className="h-px flex-1 bg-gray-200" />
                                or use your password
                                <span className="h-px flex-1 bg-gray-200" />
                            </div>
                        )}

                        {passwordEnabled && (
                        <form className="login-form" onSubmit={handleLogin}>
                            <div className="login-field">
                                <label htmlFor="email">Email address or username</label>
                                <input
                                    id="email"
                                    name="email"
                                    type="text"
                                    autoComplete="username"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </div>

                            <div className="login-field">
                                <div className="login-field-row">
                                    <label htmlFor="password">Password</label>
                                </div>
                                <div className="login-password-wrap">
                                    <input
                                        id="password"
                                        name="password"
                                        type={showPassword ? "text" : "password"}
                                        autoComplete="current-password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        className="login-toggle-password"
                                        onClick={() => setShowPassword((current) => !current)}
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                    >
                                        {showPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                                    </button>
                                </div>
                            </div>

                            <button type="submit" className="login-submit" disabled={loginRequested}>
                                {loginRequested ? "Signing in..." : "Sign In"}
                            </button>
                        </form>
                        )}

                        <p className="login-help-text">
                            Need access? Please contact your church admin to create your account.
                        </p>
                        <p className="mt-4 text-center text-xs leading-5 text-gray-500">
                            By signing in you agree to our{" "}
                            <Link to="/terms" className="font-medium text-emerald-700 underline underline-offset-2">Terms of Service</Link>
                            {" "}and{" "}
                            <Link to="/privacy" className="font-medium text-emerald-700 underline underline-offset-2">Privacy Policy</Link>.
                        </p>
                        <Link to="/" className="login-back-link">
                            Back to home
                        </Link>
                    </div>
                </section>
            </main>
        </div>
    );
};

Login.propTypes = {
    error: PropTypes.string,
    loginRequested: PropTypes.bool,
    onLoginRequested: PropTypes.func.isRequired,
};

Login.defaultProps = {
    error: "",
    loginRequested: false,
};

const mapStateToProps = (state) => ({
    error: state.auth.loginError,
    loginRequested: state.auth.loginRequested,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
    onLoginRequested,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Login);
